import { atom, useAtomValue, useSetAtom } from 'jotai';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { LocalStore } from 'shared/helpers/Store';
import { kmToMile } from 'shared/modules/loadboard/LoadboardUtils';
import { useSavedSearches } from '../../data/LoadboardSavedSearchesAPI';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';

interface LoadAlertNudge {
  isBook: boolean;
  load: PostingLoadDTO;
}

const loadAlertNudgeAtom = atom<LoadAlertNudge | undefined>(undefined);

export function useLoadAlertNudge() {
  return useAtomValue(loadAlertNudgeAtom);
}

export function useSetLoadAlertNudge() {
  return useSetAtom(loadAlertNudgeAtom);
}

const DISMISSED_LOAD_ALERT_NUDGING_QUANTITY_STORAGE_KEY =
  'load_alert_nudging_dismissed_dialog_quantity';
const DISMISSED_LOAD_ALERT_NUDGING_TIMESTAMP_STORAGE_KEY =
  'load_alert_nudging_dismissed_timestamp';
const LOAD_ALERT_REQUESTS_SEND_BEFORE_NUDGING =
  'load_alert_nudging_left_requests_send_before';

function hasTwoWeeksPassed(timestamp: string | null) {
  if (!timestamp) {
    return false;
  }

  const givenDate = DateTime.fromISO(timestamp);
  const now = DateTime.now();
  const diff = now.diff(givenDate, 'weeks').weeks;

  return diff >= 2;
}

export function dismissLoadAlertNudgingDialog() {
  const nudgedTimes = LocalStore.get(
    DISMISSED_LOAD_ALERT_NUDGING_QUANTITY_STORAGE_KEY,
  );
  const nudgingDismissedTimes = nudgedTimes ? parseInt(nudgedTimes) : 0;

  LocalStore.set(
    DISMISSED_LOAD_ALERT_NUDGING_QUANTITY_STORAGE_KEY,
    String(nudgingDismissedTimes + 1),
  );

  LocalStore.set(
    DISMISSED_LOAD_ALERT_NUDGING_TIMESTAMP_STORAGE_KEY,
    DateTime.now().toISO(),
  );
}

export function countRequestsSentBeforeLoadAlertNudging() {
  const nudgedTimes = LocalStore.get(
    DISMISSED_LOAD_ALERT_NUDGING_QUANTITY_STORAGE_KEY,
  );
  const requestsSentBeforeNudging = LocalStore.get(
    LOAD_ALERT_REQUESTS_SEND_BEFORE_NUDGING,
  );
  const isFirstDismissal = nudgedTimes === '1';
  const requestsSent = requestsSentBeforeNudging
    ? parseInt(requestsSentBeforeNudging)
    : 0;

  if (isFirstDismissal && requestsSent < 3) {
    LocalStore.set(
      LOAD_ALERT_REQUESTS_SEND_BEFORE_NUDGING,
      String(requestsSent + 1),
    );
  }
}

function shouldShowLoadAlertNudging(hasLoadAlerts: boolean) {
  const dismissedTimes = LocalStore.get(
    DISMISSED_LOAD_ALERT_NUDGING_QUANTITY_STORAGE_KEY,
  );
  const requestsSentBeforeNudging = LocalStore.get(
    LOAD_ALERT_REQUESTS_SEND_BEFORE_NUDGING,
  );

  if (hasLoadAlerts) {
    return false;
  }

  // First time nudging
  if (!dismissedTimes) {
    return true;
  }

  return dismissedTimes === '1'
    ? requestsSentBeforeNudging === '3'
    : hasTwoWeeksPassed(
        LocalStore.get(DISMISSED_LOAD_ALERT_NUDGING_TIMESTAMP_STORAGE_KEY),
      );
}

export function formatRadius(value?: number | null) {
  return value != null ? kmToMile(value, 2) : 50;
}

export function usePostingLoadAlertNudgingTrigger() {
  const setLoadAlertNudge = useSetLoadAlertNudge();
  const hasLoadAlerts = useSavedSearches().data?.length !== 0;

  return useCallback(
    (isBook: boolean, load: PostingLoadDTO) => {
      if (shouldShowLoadAlertNudging(hasLoadAlerts)) {
        setLoadAlertNudge({ load, isBook });
      }
    },
    [setLoadAlertNudge, hasLoadAlerts],
  );
}
